import { Dispatch } from 'redux';
import { api, useApi } from '../../configuration/api/apiHelper';
import {
  CategoryAttributeViewModel,
  CreateOrderCommand,
  OrderViewModel,
  OrderProposalViewModel,
  UpdateProposalByHandymanCommand,
  UpdateProposalByCustomerCommand,
  SendOrderProposalToCustomerCommand,
  SendOrderProposalToHandymanCommand,
  OrderProposalStatus,
  BrowseAvailableForHandymanOrdersQuery,
  CustomerOrderStatus,
  HandymanOrderStatus,
} from '../../configuration/api/apiSchema';

// ------------------------------
// Existing action types & logic
// ------------------------------
export enum GET_ORDERS {
  REQUEST = 'GET_ORDERS_REQUEST',
  SUCCESS = 'GET_ORDERS_SUCCESS',
}

export const getOrdersRequestAction = () => ({
  type: GET_ORDERS.REQUEST,
});

export const selectUserCategoriesSuccessAction = (
  orders: OrderViewModel[],
) => ({
  type: GET_ORDERS.SUCCESS,
  orders,
});

export const getOrders = () => {
  return async (dispatch: any) => {
    dispatch(getOrdersRequestAction());

    const response = await useApi(() => api.orders.ordersList());
    if (response.result) {
      dispatch(selectUserCategoriesSuccessAction(response.result));
    } else {
      // TODO: handle error
    }
  };
};

// ------------------------------
// Search Orders by Status
// ------------------------------
export enum SEARCH_ORDERS_BY_STATUS {
  REQUEST = 'SEARCH_ORDERS_BY_STATUS_REQUEST',
  SUCCESS = 'SEARCH_ORDERS_BY_STATUS_SUCCESS',
  FAILURE = 'SEARCH_ORDERS_BY_STATUS_FAILURE',
}

export const searchOrdersByStatusRequestAction = () => ({
  type: SEARCH_ORDERS_BY_STATUS.REQUEST,
});

export const searchOrdersByStatusSuccessAction = (
  orders: OrderViewModel[],
  proposals: OrderProposalViewModel[],
) => ({
  type: SEARCH_ORDERS_BY_STATUS.SUCCESS,
  orders,
  proposals,
});

export const searchOrdersByStatusFailureAction = (error: string) => ({
  type: SEARCH_ORDERS_BY_STATUS.FAILURE,
  error,
});

type SearchOrdersByStatusResponse = {
  orders: OrderViewModel[];
};

export const searchOrdersByStatus =
  (
    status: CustomerOrderStatus | HandymanOrderStatus,
    includeProposals: boolean,
    role: string,
  ) =>
  async (dispatch: any) => {
    dispatch(searchOrdersByStatusRequestAction());

    try {
      const response =
        role === 'customer'
          ? await useApi(() =>
              api.orders.searchList({
                customerStatus: status as CustomerOrderStatus,
                languageKey: 'en',
                includeProposals,
              }),
            )
          : await useApi(() =>
              api.orders.searchList({
                handymanStatus: status as HandymanOrderStatus,
                languageKey: 'en',
                includeProposals,
              }),
            );

      const payload = response.result;
      console.log(payload);

      const orders = Array.isArray(payload) ? payload : (payload?.orders ?? []);
      const proposals: any[] = [];

      dispatch(searchOrdersByStatusSuccessAction(orders, proposals));
    } catch (error: any) {
      dispatch(
        searchOrdersByStatusFailureAction(
          error.message || 'Failed to search orders',
        ),
      );
    }
  };

export const withdrawBalance = (amount: number) => {
  return async (dispatch: any) => {
    try {
      const response = await api.orders.withdrawCreate({ amount });

      if (!response.error) {
        console.log('✅ Withdrawal request successful.');
        // Optionally: dispatch success action or refresh user details
      } else {
        console.error('❌ Withdrawal failed:', response.error);
        // Optionally: dispatch error action
      }
    } catch (err) {
      console.error('❌ Withdrawal request error:', err);
    }
  };
};

// Other existing actions...
export const FETCH_ATTRIBUTES_REQUEST = 'FETCH_ATTRIBUTES_REQUEST';
export const FETCH_ATTRIBUTES_SUCCESS = 'FETCH_ATTRIBUTES_SUCCESS';
export const FETCH_ATTRIBUTES_FAILURE = 'FETCH_ATTRIBUTES_FAILURE';

export const CREATE_ORDER_REQUEST = 'CREATE_ORDER_REQUEST';
export const CREATE_ORDER_SUCCESS = 'CREATE_ORDER_SUCCESS';
export const CREATE_ORDER_FAILURE = 'CREATE_ORDER_FAILURE';
export const CLEAR_CREATED_ORDER = 'CLEAR_CREATED_ORDER';

export const fetchCategoryAttributes =
  (categoryKey: string) => async (dispatch: Dispatch) => {
    console.log('🚀 fetchCategoryAttributes action called for:', categoryKey);
    dispatch({ type: FETCH_ATTRIBUTES_REQUEST });
    try {
      console.log(
        '📞 Calling API: categories.attributesList with key:',
        categoryKey,
      );
      const response = await api.categories.attributesList(categoryKey, {
        languageKey: 'en',
      });
      console.log('✅ API Success! Received attributes:', response.data);
      dispatch({ type: FETCH_ATTRIBUTES_SUCCESS, payload: response.data });
    } catch (error: any) {
      console.error('❌ API Error:', error);
      dispatch({
        type: FETCH_ATTRIBUTES_FAILURE,
        payload: error.message || 'Failed to fetch attributes',
      });
    }
  };

export const createOrder =
  (orderData: CreateOrderCommand) => async (dispatch: Dispatch) => {
    dispatch({ type: CREATE_ORDER_REQUEST });
    try {
      const response = await api.orders.ordersCreate(orderData, {
        languageKey: 'en',
      });
      dispatch({ type: CREATE_ORDER_SUCCESS, payload: response.data });
    } catch (error: any) {
      dispatch({
        type: CREATE_ORDER_FAILURE,
        payload: error.message || 'Failed to create order',
      });
    }
  };

export const clearCreatedOrder = () => ({
  type: CLEAR_CREATED_ORDER,
});

export const GET_ORDER_DETAILS_REQUEST = 'GET_ORDER_DETAILS_REQUEST';
export const GET_ORDER_DETAILS_SUCCESS = 'GET_ORDER_DETAILS_SUCCESS';
export const GET_ORDER_DETAILS_FAILURE = 'GET_ORDER_DETAILS_FAILURE';

export const getOrderDetails =
  (orderId: string) => async (dispatch: Dispatch) => {
    dispatch({ type: GET_ORDER_DETAILS_REQUEST });
    try {
      const response = await api.orders.ordersDetail(orderId, {
        languageKey: 'en',
      });
      dispatch({ type: GET_ORDER_DETAILS_SUCCESS, payload: response.data });
    } catch (error: any) {
      dispatch({
        type: GET_ORDER_DETAILS_FAILURE,
        payload: error.message || 'Failed to fetch order details',
      });
    }
  };

// ---------------------------------------------------
// NEW ACTION TYPES (Handyman Proposals & Browse Orders)
// ---------------------------------------------------
export const GET_HANDYMAN_PROPOSALS_REQUEST = 'GET_HANDYMAN_PROPOSALS_REQUEST';
export const GET_HANDYMAN_PROPOSALS_SUCCESS = 'GET_HANDYMAN_PROPOSALS_SUCCESS';
export const GET_HANDYMAN_PROPOSALS_FAILURE = 'GET_HANDYMAN_PROPOSALS_FAILURE';

export const GET_HANDYMAN_ORDER_PROPOSALS_REQUEST =
  'GET_HANDYMAN_ORDER_PROPOSALS_REQUEST';
export const GET_HANDYMAN_ORDER_PROPOSALS_SUCCESS =
  'GET_HANDYMAN_ORDER_PROPOSALS_SUCCESS';
export const GET_HANDYMAN_ORDER_PROPOSALS_FAILURE =
  'GET_HANDYMAN_ORDER_PROPOSALS_FAILURE';

export const GET_HANDYMAN_AVAILABLE_ORDERS_REQUEST =
  'GET_HANDYMAN_AVAILABLE_ORDERS_REQUEST';
export const GET_HANDYMAN_AVAILABLE_ORDERS_SUCCESS =
  'GET_HANDYMAN_AVAILABLE_ORDERS_SUCCESS';
export const GET_HANDYMAN_AVAILABLE_ORDERS_FAILURE =
  'GET_HANDYMAN_AVAILABLE_ORDERS_FAILURE';

export const UPDATE_HANDYMAN_PROPOSAL_REQUEST =
  'UPDATE_HANDYMAN_PROPOSAL_REQUEST';
export const UPDATE_HANDYMAN_PROPOSAL_SUCCESS =
  'UPDATE_HANDYMAN_PROPOSAL_SUCCESS';
export const UPDATE_HANDYMAN_PROPOSAL_FAILURE =
  'UPDATE_HANDYMAN_PROPOSAL_FAILURE';

export const SEND_HANDYMAN_PROPOSAL_REQUEST = 'SEND_HANDYMAN_PROPOSAL_REQUEST';
export const SEND_HANDYMAN_PROPOSAL_SUCCESS = 'SEND_HANDYMAN_PROPOSAL_SUCCESS';
export const SEND_HANDYMAN_PROPOSAL_FAILURE = 'SEND_HANDYMAN_PROPOSAL_FAILURE';

// ---------------------------------------------------
// NEW ACTION TYPES (Customer sees proposals on an order)
// ---------------------------------------------------
export const GET_ORDER_PROPOSALS_REQUEST = 'GET_ORDER_PROPOSALS_REQUEST';
export const GET_ORDER_PROPOSALS_SUCCESS = 'GET_ORDER_PROPOSALS_SUCCESS';
export const GET_ORDER_PROPOSALS_FAILURE = 'GET_ORDER_PROPOSALS_FAILURE';

export const UPDATE_ORDER_PROPOSAL_REQUEST = 'UPDATE_ORDER_PROPOSAL_REQUEST';
export const UPDATE_ORDER_PROPOSAL_SUCCESS = 'UPDATE_ORDER_PROPOSAL_SUCCESS';
export const UPDATE_ORDER_PROPOSAL_FAILURE = 'UPDATE_ORDER_PROPOSAL_FAILURE';

export const SEND_ORDER_PROPOSAL_REQUEST = 'SEND_ORDER_PROPOSAL_REQUEST';
export const SEND_ORDER_PROPOSAL_SUCCESS = 'SEND_ORDER_PROPOSAL_SUCCESS';
export const SEND_ORDER_PROPOSAL_FAILURE = 'SEND_ORDER_PROPOSAL_FAILURE';

// ---------------------------------------------------
// HANDYMAN: Get Proposals
// ---------------------------------------------------
export const getHandymanProposals = () => async (dispatch: Dispatch) => {
  dispatch({ type: GET_HANDYMAN_PROPOSALS_REQUEST });
  try {
    const response = await api.handymanOrders.proposalsList({
      languageKey: 'en',
    });
    dispatch({ type: GET_HANDYMAN_PROPOSALS_SUCCESS, payload: response.data });
  } catch (error: any) {
    dispatch({
      type: GET_HANDYMAN_PROPOSALS_FAILURE,
      payload: error.message || 'Failed to fetch handyman proposals',
    });
  }
};

// ---------------------------------------------------
// HANDYMAN: Get Sent Proposals (handyman-initiated)
// ---------------------------------------------------
export const getHandymanSentProposals = () => async (dispatch: Dispatch) => {
  dispatch({ type: GET_HANDYMAN_PROPOSALS_REQUEST });
  try {
    const response = await api.handymanOrders.proposalsList({
      languageKey: 'en',
      isInitiatedByCustomer: false,
    });
    dispatch({ type: GET_HANDYMAN_PROPOSALS_SUCCESS, payload: response.data });
  } catch (error: any) {
    dispatch({
      type: GET_HANDYMAN_PROPOSALS_FAILURE,
      payload: error.message || 'Failed to fetch handyman sent proposals',
    });
  }
};

// ---------------------------------------------------
// HANDYMAN: Get Received Proposals (customer-initiated)
// ---------------------------------------------------
export const getHandymanReceivedProposals =
  () => async (dispatch: Dispatch) => {
    dispatch({ type: GET_HANDYMAN_PROPOSALS_REQUEST });
    try {
      const response = await api.handymanOrders.proposalsList({
        languageKey: 'en',
        isInitiatedByCustomer: true,
      });
      dispatch({
        type: GET_HANDYMAN_PROPOSALS_SUCCESS,
        payload: response.data,
      });
    } catch (error: any) {
      dispatch({
        type: GET_HANDYMAN_PROPOSALS_FAILURE,
        payload: error.message || 'Failed to fetch handyman received proposals',
      });
    }
  };

// ---------------------------------------------------
// HANDYMAN: Get Proposals for Specific Order
// ---------------------------------------------------
export const getHandymanOrderProposals =
  (orderId: string) => async (dispatch: Dispatch) => {
    dispatch({ type: GET_HANDYMAN_ORDER_PROPOSALS_REQUEST });
    try {
      const response = await api.handymanOrders.proposalsList2(orderId, {
        languageKey: 'en',
      });
      dispatch({
        type: GET_HANDYMAN_ORDER_PROPOSALS_SUCCESS,
        payload: response.data,
      });
    } catch (error: any) {
      dispatch({
        type: GET_HANDYMAN_ORDER_PROPOSALS_FAILURE,
        payload: error.message || 'Failed to fetch handyman order proposals',
      });
    }
  };

// ---------------------------------------------------
// HANDYMAN: Get Available Orders to Browse
// ---------------------------------------------------
export const getHandymanAvailableOrders =
  (filters: BrowseAvailableForHandymanOrdersQuery) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: GET_HANDYMAN_AVAILABLE_ORDERS_REQUEST });
    try {
      const response = await api.handymanOrders.availableOrdersCreate(filters);
      const data = Array.isArray(response.data)
        ? response.data
        : [response.data];
      dispatch({ type: GET_HANDYMAN_AVAILABLE_ORDERS_SUCCESS, payload: data });
    } catch (error: any) {
      dispatch({
        type: GET_HANDYMAN_AVAILABLE_ORDERS_FAILURE,
        payload: error.message || 'Failed to fetch available orders',
      });
    }
  };

// ---------------------------------------------------
// HANDYMAN: Update (Accept/Reject) a Proposal
// Note: Backend will handle auto-cancelling other proposals when status is "Confirmed"
// ---------------------------------------------------
export const updateHandymanProposal = (
  orderProposalId: string,
  status: 'Confirmed' | 'Rejected',
) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: UPDATE_HANDYMAN_PROPOSAL_REQUEST });
    try {
      const body: UpdateProposalByHandymanCommand = {
        orderProposalId: orderProposalId,
        status: status as OrderProposalStatus,
      };
      const response = await api.handymanOrders.proposalsUpdate(
        orderProposalId,
        body,
        {
          languageKey: 'en',
        },
      );
      dispatch({
        type: UPDATE_HANDYMAN_PROPOSAL_SUCCESS,
        payload: response.data,
      });
    } catch (error: any) {
      dispatch({
        type: UPDATE_HANDYMAN_PROPOSAL_FAILURE,
        payload: error.message || 'Failed to update proposal',
      });
    }
  };
};

// ---------------------------------------------------
// HANDYMAN: Send Proposal (Apply) to an Order
// ---------------------------------------------------
export const sendHandymanProposal = (
  orderId: string,
  proposedPrice?: number,
  coverLetter?: string,
) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: SEND_HANDYMAN_PROPOSAL_REQUEST });
    try {
      const body: SendOrderProposalToCustomerCommand = {
        orderId,
        proposedPrice: proposedPrice ?? null,
      };

      const response = await api.handymanOrders.sendProposalCreate(
        orderId,
        body,
        {
          languageKey: 'en',
        },
      );

      dispatch({
        type: SEND_HANDYMAN_PROPOSAL_SUCCESS,
        payload: response.data,
      });
    } catch (error: any) {
      dispatch({
        type: SEND_HANDYMAN_PROPOSAL_FAILURE,
        payload: error.message || 'Failed to send proposal',
      });
    }
  };
};
// ---------------------------------------------------
// HANDYMAN: Cancel Order
// ---------------------------------------------------
export const cancelOrder = (orderId: string) => async (dispatch: Dispatch) => {
  dispatch({ type: 'CANCEL_ORDER_REQUEST' });

  try {
    const response = await api.orders.cancelCreate(orderId);
    dispatch({ type: 'CANCEL_ORDER_SUCCESS', payload: response.data });
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'CANCEL_ORDER_FAILURE',
      payload: error.message || 'Failed to cancel order',
    });
    throw error;
  }
};
// ---------------------------------------------------
// CUSTOMER: Get Proposals for a Single Order
// ---------------------------------------------------
export const getOrderProposals =
  (orderId: string) => async (dispatch: Dispatch) => {
    dispatch({ type: GET_ORDER_PROPOSALS_REQUEST });
    try {
      const response = await api.orders.proposalsList(orderId, {
        languageKey: 'en',
      });
      dispatch({ type: GET_ORDER_PROPOSALS_SUCCESS, payload: response.data });
    } catch (error: any) {
      dispatch({
        type: GET_ORDER_PROPOSALS_FAILURE,
        payload: error.message || 'Failed to fetch order proposals',
      });
    }
  };

// ---------------------------------------------------
// CUSTOMER: Update (Approve/Reject) a Proposal
// Note: Backend will handle auto-cancelling other proposals when status is "Confirmed"
// ---------------------------------------------------
export const updateOrderProposal = (
  orderProposalId: string,
  status: 'Confirmed' | 'Rejected',
) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: UPDATE_ORDER_PROPOSAL_REQUEST });
    try {
      const body: UpdateProposalByCustomerCommand = {
        orderProposalId: orderProposalId,
        status: status as OrderProposalStatus,
      };
      const response = await api.orders.customerProposalsUpdate(
        orderProposalId,
        body,
        {
          languageKey: 'en',
        },
      );
      dispatch({ type: UPDATE_ORDER_PROPOSAL_SUCCESS, payload: response.data });
    } catch (error: any) {
      dispatch({
        type: UPDATE_ORDER_PROPOSAL_FAILURE,
        payload: error.message || 'Failed to update proposal',
      });
    }
  };
};

// ---------------------------------------------------
// CUSTOMER: Send Proposal to a specific Handyman
// ---------------------------------------------------
export const sendOrderProposal = (orderId: string, handymanId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: SEND_ORDER_PROPOSAL_REQUEST });
    try {
      const body: SendOrderProposalToHandymanCommand = {
        orderId,
        handymanId,
      };
      const response = await api.orders.sendProposalCreate(orderId, body, {
        languageKey: 'en',
      });
      dispatch({ type: SEND_ORDER_PROPOSAL_SUCCESS, payload: response.data });
      return response.data;
    } catch (error: any) {
      dispatch({
        type: SEND_ORDER_PROPOSAL_FAILURE,
        payload: error.message || 'Failed to send proposal to handyman',
      });
      throw error;
    }
  };
};

export const CREATE_OR_GET_CHAT_REQUEST = 'CREATE_OR_GET_CHAT_REQUEST';
export const CREATE_OR_GET_CHAT_SUCCESS = 'CREATE_OR_GET_CHAT_SUCCESS';
export const CREATE_OR_GET_CHAT_FAILURE = 'CREATE_OR_GET_CHAT_FAILURE';

/**
 * Creates or retrieves a chat between the specified participant IDs,
 * and returns the chatId (string GUID).
 */
export const createOrGetChat = (participantIds: string[]) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: CREATE_OR_GET_CHAT_REQUEST });
    try {
      const payload = { participantIds };
      const response = await api.chats.createOrGetChatCreate(payload);

      // Backend returns Guid (chat ID) as string, or ChatMessageViewModel with chatId property
      // Handle both cases for robustness
      let chatId: string | null = null;

      if (typeof response.data === 'string') {
        // If it's a string (Guid), use it directly
        chatId = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // If it's an object, try to extract chatId or id
        chatId =
          (response.data as any).chatId || (response.data as any).id || null;
      }

      dispatch({
        type: CREATE_OR_GET_CHAT_SUCCESS,
        payload: response.data,
      });

      return chatId; // Return the chatId string for navigation
    } catch (error: any) {
      dispatch({
        type: CREATE_OR_GET_CHAT_FAILURE,
        payload: error.message || 'Failed to create or retrieve chat',
      });
      return null;
    }
  };
};

// 1) Action Types
export const MARK_ORDER_AS_DONE_REQUEST = 'MARK_ORDER_AS_DONE_REQUEST';
export const MARK_ORDER_AS_DONE_SUCCESS = 'MARK_ORDER_AS_DONE_SUCCESS';
export const MARK_ORDER_AS_DONE_FAILURE = 'MARK_ORDER_AS_DONE_FAILURE';

export const COMPLETE_ORDER_REQUEST = 'COMPLETE_ORDER_REQUEST';
export const COMPLETE_ORDER_SUCCESS = 'COMPLETE_ORDER_SUCCESS';
export const COMPLETE_ORDER_FAILURE = 'COMPLETE_ORDER_FAILURE';

// 2) Thunk: Handyman -> Mark order as done
export const markOrderAsDone = (orderId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: MARK_ORDER_AS_DONE_REQUEST });
    try {
      // your new endpoint call
      const response = await api.handymanOrders.markAsDoneCreate(orderId, {
        languageKey: 'en',
      });
      dispatch({ type: MARK_ORDER_AS_DONE_SUCCESS, payload: response.data });
    } catch (error: any) {
      dispatch({
        type: MARK_ORDER_AS_DONE_FAILURE,
        payload: error.message || 'Failed to mark order as done',
      });
    }
  };
};

// 3) Thunk: Customer -> Complete order
export const completeOrder = (orderId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: COMPLETE_ORDER_REQUEST });
    try {
      // your new endpoint call
      const response = await api.orders.completeCreate(orderId, {
        languageKey: 'en',
      });
      dispatch({ type: COMPLETE_ORDER_SUCCESS, payload: response.data });
    } catch (error: any) {
      dispatch({
        type: COMPLETE_ORDER_FAILURE,
        payload: error.message || 'Failed to complete order',
      });
    }
  };
};

export const CREATE_PAYMENT_INTENT_REQUEST = 'CREATE_PAYMENT_INTENT_REQUEST';
export const CREATE_PAYMENT_INTENT_SUCCESS = 'CREATE_PAYMENT_INTENT_SUCCESS';
export const CREATE_PAYMENT_INTENT_FAILURE = 'CREATE_PAYMENT_INTENT_FAILURE';

export const createPaymentIntent = (orderId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: CREATE_PAYMENT_INTENT_REQUEST });
    try {
      const response = await useApi(() =>
        api.orders.createPaymentIntentCreate(orderId, { orderId }),
      );
      dispatch({
        type: CREATE_PAYMENT_INTENT_SUCCESS,
        payload: response.result,
      });
      return response.result;
    } catch (error: any) {
      dispatch({
        type: CREATE_PAYMENT_INTENT_FAILURE,
        payload: error.message || 'Failed to create payment intent',
      });
      throw error;
    }
  };
};

export const CREATE_CHECKOUT_SESSION_REQUEST =
  'CREATE_CHECKOUT_SESSION_REQUEST';
export const CREATE_CHECKOUT_SESSION_SUCCESS =
  'CREATE_CHECKOUT_SESSION_SUCCESS';
export const CREATE_CHECKOUT_SESSION_FAILURE =
  'CREATE_CHECKOUT_SESSION_FAILURE';

export const createCheckoutSession = (orderId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: CREATE_CHECKOUT_SESSION_REQUEST });
    try {
      const response = await api.orders.createCheckoutSessionCreate(orderId, {
        orderId,
      });
      dispatch({
        type: CREATE_CHECKOUT_SESSION_SUCCESS,
        payload: response.data,
      });
      return response.data;
    } catch (error: any) {
      dispatch({
        type: CREATE_CHECKOUT_SESSION_FAILURE,
        payload: error.message || 'Failed to create checkout session',
      });
      throw error;
    }
  };
};
